import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Search, X, Check } from 'lucide-react';
import type { AppDispatch } from '@/store/store';
import { 
  searchPatients, 
  selectFilteredPatients, 
  selectIsSearching,
  selectPatientsError 
} from '@/store/slices/patientsSlice';
import type { Patient } from '@/services/patientsService';

interface PatientSearchInputProps {
  selectedPatients?: string[];
  onPatientsSelect: (patientIds: string[]) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  className?: string;
  allowMultiple?: boolean;
}

export const PatientSearchInput: React.FC<PatientSearchInputProps> = ({
  selectedPatients = [],
  onPatientsSelect,
  placeholder = "Search and select patients...",
  label = "Patients",
  required = false,
  className = "",
  allowMultiple = true
}) => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Redux selectors
  const patients = useSelector(selectFilteredPatients);
  const isSearching = useSelector(selectIsSearching);
  const error = useSelector(selectPatientsError);
  
  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPatientsData, setSelectedPatientsData] = useState<Patient[]>([]);
  
  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  
  // Keep track of all patients we've seen to maintain selected patient data
  const [allSeenPatients, setAllSeenPatients] = useState<Patient[]>([]);
  
  // Update our cache of all seen patients whenever new patients come in
  useEffect(() => {
    if (patients.length > 0) {
      setAllSeenPatients(prevSeen => {
        const newPatients = patients.filter(p => !prevSeen.some(seen => seen.id === p.id));
        return [...prevSeen, ...newPatients];
      });
    }
  }, [patients]);
  
  // Find selected patients data from our cache, not just current search results
  useEffect(() => {
    if (selectedPatients.length > 0) {
      const patientsData = selectedPatients.map(id => 
        allSeenPatients.find(p => p.id === id)
      ).filter(Boolean) as Patient[];
      setSelectedPatientsData(patientsData);
    } else {
      setSelectedPatientsData([]);
    }
  }, [selectedPatients, allSeenPatients]);
  
  // Debounced search function - same as Patients page
  const debouncedSearch = (term: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      if (term.trim()) {
        console.log('[PatientSearchInput] Searching for:', term);
        dispatch(searchPatients(term.trim()));
      }
    }, 300);
  };
  
  // Handle input change - keep search separate from selections
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Show dropdown and search
    if (value.trim()) {
      setIsOpen(true);
      debouncedSearch(value);
    } else {
      setIsOpen(false);
    }
  };
  
  // Handle patient selection
  const handlePatientSelect = (patient: Patient) => {
    // Always use multi-select logic
    const currentIds = selectedPatients;
    const isAlreadySelected = currentIds.includes(patient.id);
    
    let newSelectedIds: string[];
    if (isAlreadySelected) {
      // Remove from selection
      newSelectedIds = currentIds.filter(id => id !== patient.id);
    } else {
      // Add to selection
      newSelectedIds = [...currentIds, patient.id];
      
      // Make sure this patient is in our cache
      setAllSeenPatients(prevSeen => {
        if (!prevSeen.some(seen => seen.id === patient.id)) {
          return [...prevSeen, patient];
        }
        return prevSeen;
      });
    }
    
    onPatientsSelect(newSelectedIds);
    
    // Clear search term after selection
    setSearchTerm('');
  };
  
  // Remove patient from selection
  const handleRemovePatient = (patientId: string) => {
    const newSelectedIds = selectedPatients.filter(id => id !== patientId);
    onPatientsSelect(newSelectedIds);
  };
  
  // Format patient display
  const formatPatientDisplay = (patient: Patient) => {
    const name = `${patient.first_name} ${patient.last_name}`;
    if (patient.date_of_birth) {
      return `${name} (${patient.date_of_birth})`;
    }
    return name;
  };
  
  // Handle input focus
  const handleInputFocus = () => {
    if (searchTerm.trim()) {
      setIsOpen(true);
    }
  };
  
  // Handle input blur (with delay to allow for dropdown clicks)
  const handleInputBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };
  
  // Clear search term
  const handleClear = () => {
    setSearchTerm('');
    setIsOpen(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);
  
  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <Label className="text-sm font-lato font-medium text-vitl-navy">
          {label} {required && '*'}
        </Label>
      )}
      
      {/* Selected Patients Display */}
      {selectedPatientsData.length > 0 && (
        <div className="mb-2 space-y-1">
          {selectedPatientsData.map((patient) => (
            <div key={patient.id} className="p-2 bg-emerald-50 border-l-4 border-l-emerald-500 border border-emerald-200 rounded shadow-sm flex items-center justify-between">
              <span className="font-lato font-medium text-vitl-navy text-sm">
                {formatPatientDisplay(patient)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemovePatient(patient.id)}
                className="h-5 w-5 p-0 text-gray-400 hover:text-gray-600"
              >
                <X className="w-2 h-2" />
              </Button>
            </div>
          ))}
        </div>
      )}
      
      {/* Search Input and Dropdown */}
      <div className="relative">
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            
            {/* Show loading spinner when searching */}
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-vitl-green w-4 h-4 animate-spin" />
            )}
            
            <Input
              ref={inputRef}
              id="patient-search"
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={placeholder}
              autoComplete="off"
              className={`pl-10 ${isSearching ? 'pr-10' : 'pr-4'} h-12 bg-gray-50 border-gray-200 font-lato focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-emerald-400 focus:outline-none`}
            />
          </div>
          
          {/* Dropdown Results */}
          {isOpen && (
            <div 
              ref={dropdownRef}
              className="absolute z-50 w-full mt-1 bg-white border border-l-4 border-l-emerald-500 border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
            >
              {isSearching && (
                <div className="px-4 py-3 text-sm text-gray-500 flex items-center">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Searching patients...
                </div>
              )}
              
              {error && (
                <div className="px-4 py-3 text-sm text-red-600">
                  Error: {error}
                </div>
              )}
              
              {!isSearching && !error && patients.length === 0 && searchTerm.trim() && (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No patients found matching "{searchTerm}"
                </div>
              )}
              
              {!isSearching && !error && patients.length > 0 && (
                <>
                  {patients.map((patient) => {
                    const isSelected = selectedPatients.includes(patient.id);
                      
                    return (
                      <button
                        key={patient.id}
                        type="button"
                        onClick={() => handlePatientSelect(patient)}
                        className={`w-full px-4 py-3 text-left hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none border-b border-gray-100 last:border-b-0 ${
                          isSelected ? 'bg-emerald-100' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-lato font-medium text-vitl-navy">
                              {patient.first_name} {patient.last_name}
                            </p>
                            <p className="font-lato text-sm text-gray-600">
                              {patient.email}
                            </p>
                            {patient.date_of_birth && (
                              <p className="font-lato text-xs text-gray-500">
                                DOB: {patient.date_of_birth}
                              </p>
                            )}
                          </div>
                          {isSelected && (
                            <Check className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientSearchInput;